import { atom, atomFamily, DefaultValue, selector, selectorFamily } from 'recoil';
import type { HoldingsBreakdownTypeEnum, HoldingsCategory, HoldingsCategoryId, ImageDetails, Scenario } from 'venn-api';
import type {
  DataGridSizeType,
  EditorValue,
  GeoLevelFilterType,
  PortfolioStrategyFilterType,
  SectorLevelFilterType,
} from 'venn-components';
import type { CustomBlockTypeEnum, SelectableCustomNotablePeriod } from 'venn-utils';
import { isTreeBlock } from 'venn-utils';
import { resetOnStudioReset } from '../../effects/signalEffects';
import { unsavedChangesEffect } from '../../effects/unsavedChangesEffect';
import { holdingsCategoriesTree } from '../globalConfig';
import { studioBlockCustomWidth } from '../grid';
import { type BlockId, type CustomViewOptions, type GlobalCustomViewOptions, LayoutVersion } from '../types';
import {
  allocatorAnalysisSubject,
  openAllocatorSubject,
  openAllocatorSubjectConfig,
  openPrivateAllocatorConfig,
} from './allocator';
import { blockAllFactorsSelected } from './blockConfig';
import { blockCustomMetricSettingsState, blockSettings } from './blockSettings';
import { colorIndexSubjectTupleState } from './colors';
import {
  studioCustomFooterText,
  studioHideFooterDisclaimer,
  studioHideFooterPagination,
  studioHideHeaderCompanyName,
  studioHideHeaderLogo,
  studioHideHeaderViewName,
  viewPages,
} from './print';
import { blockPrivateAssetIsCumulative, blockPrivateDefaultAsOfDateBehavior } from './privateAssets';
import { viewHideBenchmark, viewSectionName, viewSubtitle, viewTitle } from './viewOptions';
import { customField_gridData } from './customMetrics';
import { blockSelectedPeerGroupId } from './peerGroups';

export const convertHoldingsBlockTypeToBreakdown = (blockType: CustomBlockTypeEnum): HoldingsBreakdownTypeEnum => {
  switch (blockType) {
    case 'ASSET_EXPOSURE':
      return 'ASSET';
    case 'SECTOR_EXPOSURE':
      return 'SECTOR';
    case 'GEOGRAPHY_EXPOSURE':
      return 'REGION';
    default: {
      throw new Error(`Invalid holdings block type: ${blockType}`);
    }
  }
};

export const blockScenarios = atomFamily<Scenario[], BlockId>({
  key: 'blockScenarios',
  default: [],
  effects: [unsavedChangesEffect],
});

export const viewSelectedNotablePeriods = atomFamily<number[] | undefined, BlockId>({
  key: 'viewSelectedNotablePeriods',
  default: undefined,
  effects: [unsavedChangesEffect],
});

export const viewSelectedInvestmentGroupInformation = atomFamily<string[] | undefined, BlockId>({
  key: 'viewSelectedInvestmentGroupInformation',
  default: undefined,
  effects: [unsavedChangesEffect],
});

export const viewCustomNotablePeriods = atomFamily<SelectableCustomNotablePeriod[] | undefined, BlockId>({
  key: 'viewCustomNotablePeriods',
  default: undefined,
  effects: [unsavedChangesEffect],
});

export const viewDataGridSizeType = atomFamily<DataGridSizeType | undefined, BlockId>({
  key: 'viewDataGridSizeType',
  default: undefined,
  effects: [unsavedChangesEffect],
});

export const portfolioStrategyFilterType = atomFamily<PortfolioStrategyFilterType | undefined, BlockId>({
  key: 'portfolioStrategyFilterType',
  default: undefined,
  effects: [unsavedChangesEffect],
});

export const geoLevelFilterType = atomFamily<GeoLevelFilterType | undefined, BlockId>({
  key: 'geoLevelFilterType',
  default: undefined,
  effects: [unsavedChangesEffect],
});

export const sectorLevelFilterType = atomFamily<SectorLevelFilterType | undefined, BlockId>({
  key: 'sectorLevelFilterType',
  default: undefined,
  effects: [unsavedChangesEffect],
});

export const viewCoverPageImage = atom<string | undefined>({
  key: 'viewCoverPageImage',
  default: undefined,
  effects: [unsavedChangesEffect, resetOnStudioReset],
});

export const viewImages = atom<ImageDetails[] | undefined>({
  key: 'viewImages',
  default: undefined,
  effects: [unsavedChangesEffect, resetOnStudioReset],
});

export const viewRichText = atomFamily<EditorValue | undefined, BlockId>({
  key: 'viewRichText',
  default: undefined,
  effects: [unsavedChangesEffect],
});

export const viewShowVenncast = atomFamily<boolean | undefined, BlockId>({
  key: 'viewShowVenncast',
  default: undefined,
  effects: [unsavedChangesEffect],
});

export const viewImageId = atomFamily<string | undefined, BlockId>({
  key: 'viewImageId',
  default: undefined,
  effects: [unsavedChangesEffect],
});

export const viewPrimaryColor = atom<string>({
  key: 'viewPrimaryColor',
  default: '#1c5af3',
  effects: [unsavedChangesEffect, resetOnStudioReset],
});

const blockHoldingsCategoryIds = atomFamily<HoldingsCategoryId[] | undefined, BlockId>({
  key: 'blockHoldingsCategoryIds',
  default: undefined,
  effects: [unsavedChangesEffect],
});

/** true iff the current reports layout has been changed since the view has been opened */
export const hasChangedLayoutState = atom<boolean>({
  key: 'hasChangedLayoutState',
  default: false,
  effects: [resetOnStudioReset],
});

/** What layout version the current report is using */
export const layoutVersionState = atom<LayoutVersion>({
  key: 'layoutVersionState',
  default: LayoutVersion.LEGACY,
  effects: [unsavedChangesEffect, resetOnStudioReset],
});

export const filterHoldingsToLevel = (holdings: HoldingsCategory[], level: number): HoldingsCategory[] => {
  if (level <= 0) {
    return [];
  }

  return holdings.map((holding) => ({
    ...holding,
    children: filterHoldingsToLevel(holding.children, level - 1),
  }));
};
export const allHoldingsCategories = selectorFamily<HoldingsCategory[], BlockId>({
  key: 'allHoldingsCategories',
  get:
    (id) =>
    ({ get }) => {
      const settings = get(blockSettings(id));
      return get(holdingsCategoriesTree(convertHoldingsBlockTypeToBreakdown(settings.customBlockType)));
    },
});

export const holdingsCategories = selectorFamily<HoldingsCategory[], BlockId>({
  key: 'holdingsCategories',
  get:
    (id) =>
    ({ get }) => {
      const allCategories = get(allHoldingsCategories(id));
      const blockType = get(blockSettings(id)).customBlockType;
      const selectedCategoryIds = get(blockHoldingsCategoryIds(id))?.map((id) => id.id);
      if (blockType === 'GEOGRAPHY_EXPOSURE') {
        return allCategories;
      }
      const getCategories = (allCategories: HoldingsCategory[]) => {
        if (selectedCategoryIds) {
          const settings = get(blockSettings(id));
          const isTree = settings.customBlockType === 'SECTOR_EXPOSURE';
          if (isTree) {
            return allCategories.map((category) => ({
              ...category,
              children: category.children.filter((child) => selectedCategoryIds.includes(child.id.id)),
            }));
          }
          return allCategories.filter((category) => selectedCategoryIds.includes(category.id.id));
        }
        return allCategories;
      };

      return getCategories(allCategories);
    },
  set:
    (id) =>
    ({ get, set }, newValue) => {
      // no idea what we do with DefaultValue
      if (newValue instanceof DefaultValue) {
        // TODO: we should propogate reset and call reset on ALL blockHoldingsCategoryIds atoms
        return;
      }

      // select all
      const allCategories = get(allHoldingsCategories(id));
      const settings = get(blockSettings(id));

      const allCategoriesToCheck = isTreeBlock(settings.customBlockType)
        ? allCategories.flatMap((category) => category.children).map((category) => category.id)
        : allCategories.map((category) => category.id);

      const selectedIds = newValue.map((category) => category.id);
      const selectedIdStrings = selectedIds.map((id) => id.id);
      if (allCategoriesToCheck.every((id) => selectedIdStrings.includes(id.id))) {
        // undefined means all categories selected
        set(blockHoldingsCategoryIds(id), undefined);
        return;
      }

      // if not select all then either filter to set of IDs or an empty array for select none
      set(blockHoldingsCategoryIds(id), selectedIds);
    },
});

export const customViewOptions = selectorFamily<CustomViewOptions, BlockId>({
  key: 'customViewOptions',
  get:
    (id) =>
    ({ get }) => {
      const selectedNotablePeriods = get(viewSelectedNotablePeriods(id));
      const selectedInvestmentGroupInformation = get(viewSelectedInvestmentGroupInformation(id));
      const customNotablePeriods = get(viewCustomNotablePeriods(id));
      const hideBenchmark = get(viewHideBenchmark(id));
      const dataGridSizeType = get(viewDataGridSizeType(id));
      const portfolioStrategyFilter = get(portfolioStrategyFilterType(id));
      const geoLevelFilter = get(geoLevelFilterType(id));
      const sectorLevelFilter = get(sectorLevelFilterType(id));
      const BLOCK_WIDTH = get(studioBlockCustomWidth(id));
      const title = get(viewTitle(id));
      const sectionName = get(viewSectionName(id));
      const subtitle = get(viewSubtitle(id));
      const richText = get(viewRichText(id));
      const showVenncast = get(viewShowVenncast(id));
      const imageId = get(viewImageId(id));
      const scenarios = get(blockScenarios(id));
      const allFactorsSelected = get(blockAllFactorsSelected(id));
      const holdingsCategoryIds = get(blockHoldingsCategoryIds(id));
      const privateAssetIsCumulative = get(blockPrivateAssetIsCumulative(id));
      const privateDefaultAsOfDateBehavior = get(blockPrivateDefaultAsOfDateBehavior(id));
      const customMetricsData = get(customField_gridData(id));
      const selectedPeerGroupId = get(blockSelectedPeerGroupId(id));
      const customMetricsSettings = get(blockCustomMetricSettingsState(id));

      return {
        selectedNotablePeriods,
        selectedInvestmentGroupInformation,
        customNotablePeriods,
        hideBenchmark,
        dataGridSizeType,
        portfolioStrategyFilter,
        geoLevelFilter,
        sectorLevelFilter,
        BLOCK_WIDTH,
        title,
        sectionName,
        subtitle,
        richText,
        showVenncast,
        imageId,
        scenarios,
        allFactorsSelected,
        holdingsCategoryIds,
        privateAssetIsCumulative,
        privateDefaultAsOfDateBehavior,
        customMetricGrid: customMetricsData,
        selectedPeerGroupId,
        customMetricsSettings,
      };
    },
  set:
    (id) =>
    ({ set }, value) => {
      if (value instanceof DefaultValue) {
        return;
      }
      set(viewSelectedNotablePeriods(id), value.selectedNotablePeriods);
      set(viewSelectedInvestmentGroupInformation(id), value.selectedInvestmentGroupInformation);
      set(viewCustomNotablePeriods(id), value.customNotablePeriods);
      set(viewHideBenchmark(id), value.hideBenchmark);
      set(viewDataGridSizeType(id), value.dataGridSizeType);
      set(portfolioStrategyFilterType(id), value.portfolioStrategyFilter);
      set(geoLevelFilterType(id), value.geoLevelFilter);
      set(sectorLevelFilterType(id), value.sectorLevelFilter);
      value.BLOCK_WIDTH && set(studioBlockCustomWidth(id), value.BLOCK_WIDTH);
      set(viewTitle(id), value.title);
      set(viewSectionName(id), value.sectionName);
      set(viewSubtitle(id), value.subtitle);
      set(viewRichText(id), value.richText);
      set(viewShowVenncast(id), value.showVenncast);
      set(viewImageId(id), value.imageId);
      value.scenarios && set(blockScenarios(id), value.scenarios);
      value.allFactorsSelected && set(blockAllFactorsSelected(id), value.allFactorsSelected);
      set(blockHoldingsCategoryIds(id), value.holdingsCategoryIds);
      set(blockPrivateAssetIsCumulative(id), value.privateAssetIsCumulative);
      set(blockPrivateDefaultAsOfDateBehavior(id), !!value.privateDefaultAsOfDateBehavior);
      value.customMetricGrid && set(customField_gridData(id), value.customMetricGrid);
      set(blockSelectedPeerGroupId(id), value.selectedPeerGroupId);
      value.customMetricsSettings && set(blockCustomMetricSettingsState(id), value.customMetricsSettings);
    },
});

// TODO(VER-87): PERFORMANCE: this is basically just a helper that could be split into two recoil callbacks to improve performance
export const globalCustomViewOptions = selector<GlobalCustomViewOptions>({
  key: 'customViewOptions',
  get: ({ get }) => {
    const pages = get(viewPages);
    const coverPageImage = get(viewCoverPageImage);
    const images = get(viewImages);
    const hide_header_company_name = get(studioHideHeaderViewName);
    const hide_header_logo = get(studioHideHeaderCompanyName);
    const hide_header_view_name = get(studioHideHeaderLogo);
    const hide_footer_pagination = get(studioHideFooterPagination);
    const hide_footer_disclaimer = get(studioHideFooterDisclaimer);
    const custom_footer_text = get(studioCustomFooterText);
    const primaryColor = get(viewPrimaryColor);
    const allocatorConfig = get(openAllocatorSubjectConfig);
    const colorIndexSubjectMapping = get(colorIndexSubjectTupleState);
    const layoutVersion = get(layoutVersionState);
    const privateAllocatorConfig = get(openPrivateAllocatorConfig);
    return {
      pages,
      coverPageImage,
      images,
      hide_header_company_name,
      hide_header_logo,
      hide_header_view_name,
      hide_footer_pagination,
      hide_footer_disclaimer,
      custom_footer_text,
      primaryColor,
      allocatorConfig,
      colorIndexSubjectMapping,
      layoutVersion,
      privateAllocatorConfig,
    };
  },
  set: ({ get, set }, value) => {
    if (value instanceof DefaultValue) {
      set(viewPages, value);
      set(viewCoverPageImage, value);
      set(viewImages, value);
      set(studioHideHeaderViewName, value);
      set(studioHideHeaderCompanyName, value);
      set(studioHideHeaderLogo, value);
      set(studioHideFooterPagination, value);
      set(studioHideFooterDisclaimer, value);
      set(studioCustomFooterText, value);
      set(viewPrimaryColor, value);
      set(allocatorAnalysisSubject(get(openAllocatorSubject)), value);
      set(openAllocatorSubject, value);
      set(colorIndexSubjectTupleState, value);
      set(layoutVersionState, value);
      return;
    }
    value.pages && set(viewPages, value.pages);
    set(viewCoverPageImage, value.coverPageImage);
    set(viewImages, value.images);
    value.hide_header_company_name && set(studioHideHeaderViewName, value.hide_header_company_name);
    value.hide_header_logo && set(studioHideHeaderCompanyName, value.hide_header_logo);
    value.hide_header_view_name && set(studioHideHeaderLogo, value.hide_header_view_name);
    value.hide_footer_pagination && set(studioHideFooterPagination, value.hide_footer_pagination);
    value.hide_footer_disclaimer && set(studioHideFooterDisclaimer, value.hide_footer_disclaimer);
    value.custom_footer_text && set(studioCustomFooterText, value.custom_footer_text);
    value.primaryColor && set(viewPrimaryColor, value.primaryColor);
    value.allocatorConfig && set(openAllocatorSubjectConfig, value.allocatorConfig);
    value.colorIndexSubjectMapping && set(colorIndexSubjectTupleState, value.colorIndexSubjectMapping);
    value.layoutVersion && set(layoutVersionState, value.layoutVersion);
    value.privateAllocatorConfig && set(openPrivateAllocatorConfig, value.privateAllocatorConfig);
  },
});
